import { Box, CircularProgress, Stack } from "@mui/joy";
import React, { useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import Link from "../../components/common/Link";
import useGetUrls from "../../hooks/useGetUrls";
import GoogleLoginButtonPromotion from "../../components/user/GoogleLoginButtonPromotion";
import AppleLoginButtonPromotion from "../../components/user/AppleLoginButtonPromotion";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import { motion, PanInfo, useAnimationControls } from "framer-motion";

import promotion1 from "../../static/image/promotion/login/1.png";
import promotion2 from "../../static/image/promotion/login/2.png";
import promotion3 from "../../static/image/promotion/login/3.png";
import promotion4 from "../../static/image/promotion/login/4.png";
import promotion5 from "../../static/image/promotion/login/5.png";
import promotion6 from "../../static/image/promotion/login/6.png";
import promotion7 from "../../static/image/promotion/login/7.png";
import promotion8 from "../../static/image/promotion/login/8.png";
import promotion9 from "../../static/image/promotion/login/9.png";
import promotion10 from "../../static/image/promotion/login/10.png";
import distancingLogo from "../../static/image/common/distancing_logo.png";
import dividerImg from "../../static/image/login/login_divider.svg";
import KakaoLoginButtonPromotion from "../../components/user/KakaoLoginButtonPromotion";
import useSafeAreaColor from "../../hooks/useSafeAreaColor";

const carouselItems = [
  { title: "매일 주어지는 활동지", image: promotion1 },
  { title: "이론 배우기", image: promotion2 },
  { title: "전담 코치의 리딩", image: promotion3 },
  { title: "패턴 발견하기", image: promotion4 },
  { title: "명상 훈련", image: promotion5 },
  { title: "생각 다루기", image: promotion6 },
  { title: "추적하며 관리하기", image: promotion7 },
  { title: "100일간의 여정", image: promotion8 },
  { title: "나 이해하기", image: promotion9 },
  { title: "가치 발견하기", image: promotion10 },
];

const ITEM_WIDTH = 226;
const ITEM_GAP = 12.5;
const MAX_VIEWPORT_WIDTH = ITEM_WIDTH * 1.8 + ITEM_GAP * 2;

export default function LoginPromotion() {
  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  const { isChecking } = useAutoLoginPromotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  // 캐러셀 (framer-motion drag) 함정 메모 — 수정 시 주의
  //
  // 1. drag prop 을 React state 로 토글하지 말 것.
  //    React 18 자동 배칭으로 setState 가 핸들러 종료 후에야 반영되어,
  //    framer-motion 이 PointerDown 시점의 옛 drag prop 값으로 동작 여부를
  //    결정함 → 첫 swipe 무시되는 패턴 발생.
  //    → drag="x" 로 고정하고 imperative controls 로 제어할 것.
  //
  // 2. drag + animate prop 조합 + dragMomentum={false} 일 때, drag 종료 후
  //    motion value 가 animate target 으로 자동 복귀하지 않음. animate target
  //    이 동일하면 reanimate 안 됨 → 카드가 중간 위치에 멈춤.
  //    → useAnimationControls 로 drag 종료 시 항상 controls.start 호출.
  //
  // 3. touch-action: pan-y 만으로는 대각선 swipe 시 가로 drag 와 세로
  //    native scroll 이 동시에 일어남. drag 시점에 부모 scrollContainer 의
  //    overflowY 를 hidden 으로 잠가야 함.
  //
  // 4. 단, onDragStart 의 trigger 임계값은 방향 무관 (~3-5px). 약한 가로
  //    성분만 있어도 호출됨. onDragStart 에서 무조건 잠그면 세로 swipe 도
  //    잠겨 첫 시도가 무시됨 (1 번 패턴 회귀).
  //    → onDragStart 안에서 |offset.x| > |offset.y| (가로 우세) 일 때만 잠금.
  const controls = useAnimationControls();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    controls.start({
      x: -currentIndex * (ITEM_WIDTH + ITEM_GAP),
      transition: { type: "spring", stiffness: 300, damping: 30 },
    });
  }, [currentIndex, controls]);

  const backgroundColor = theme.vars.palette.Background.Level2;

  useSafeAreaColor(backgroundColor);

  const trackPromotion = useMixpanelMKT({
    eventName: "로그인 페이지 진입",
    version: "v4",
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);
    useEffect(() => {
      if (ref.current) return;
      ref.current = true;
      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackPromotion();
  });

  const handleDragStart = (_: any, info: PanInfo) => {
    // 가로 성분이 세로 성분보다 우세할 때만 페이지 세로 스크롤 잠금.
    // 세로 의도의 swipe 에서는 brower native scroll 을 그대로 허용.
    if (Math.abs(info.offset.x) > Math.abs(info.offset.y)) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.overflowY = "hidden";
      }
    }
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflowY = "auto";
    }

    const threshold = 50;
    const velocityThreshold = 500;

    let nextIndex = currentIndex;
    if (
      (info.offset.x < -threshold || info.velocity.x < -velocityThreshold) &&
      currentIndex < carouselItems.length - 1
    ) {
      nextIndex = currentIndex + 1;
    } else if (
      (info.offset.x > threshold || info.velocity.x > velocityThreshold) &&
      currentIndex > 0
    ) {
      nextIndex = currentIndex - 1;
    }

    if (nextIndex !== currentIndex) {
      setCurrentIndex(nextIndex);
      // useEffect 가 controls.start 트리거
    } else {
      // currentIndex 변화 없음 → 원래 카드 위치로 명시적으로 spring 복귀
      controls.start({
        x: -currentIndex * (ITEM_WIDTH + ITEM_GAP),
        transition: { type: "spring", stiffness: 300, damping: 30 },
      });
    }
  };

  const childrenType = (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      spacing={4}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        pt: 4,
      }}
    >
      <img
        src={distancingLogo}
        alt="distancing logo"
        draggable={false}
        style={{ width: "138px", height: "auto", flexShrink: 0 }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "320px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 4 },
          flexShrink: 0,
        }}
      >
        <Stack spacing={"12px"} sx={{ textAlign: "center", width: "100%" }}>
          <CustomTypography
            variant="H1"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 800,
              whiteSpace: "pre-wrap",
            }}
          >
            {"맞춤 프로그램 결과를\n지금 만나보세요"}
          </CustomTypography>
          <CustomTypography
            variant="Subtitle"
            sx={{
              color: theme.vars.palette.Content.Medium,
              fontWeight: 500,
              whiteSpace: "pre-wrap",
            }}
          >
            {"효과가 입증된 비약물 심리치료"}
          </CustomTypography>
        </Stack>

        <Stack
          spacing={"6px"}
          sx={{
            width: "100%",
          }}
          alignSelf="center"
        >
          <KakaoLoginButtonPromotion />
          <AppleLoginButtonPromotion />
          <GoogleLoginButtonPromotion />
        </Stack>
      </Box>

      <img
        src={dividerImg}
        alt={"empty"}
        style={{
          width: "100%",
          maxWidth: "320px",
        }}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          touchAction: "pan-y",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: `${MAX_VIEWPORT_WIDTH}px`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <motion.div
            drag="x"
            dragDirectionLock
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            dragMomentum={false}
            dragElastic={0.1}
            dragConstraints={{
              left: -(carouselItems.length - 1) * (ITEM_WIDTH + ITEM_GAP),
              right: 0,
            }}
            initial={{ x: 0 }}
            animate={controls}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              cursor: "grab",
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: "pan-y",
            }}
            whileTap={{ cursor: "grabbing" }}
          >
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
            {carouselItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: `0 0 ${ITEM_WIDTH}px`,
                  marginRight: `${ITEM_GAP}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  boxSizing: "border-box",
                }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: index === currentIndex ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <CustomTypography
                    variant="Body"
                    sx={{
                      py: 0,
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {item.title}
                  </CustomTypography>
                </motion.div>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
          </motion.div>
        </Box>
      </Box>
    </Stack>
  );

  if (isChecking) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: backgroundColor,
        }}
      >
        <CircularProgress color="neutral" />
      </Box>
    );
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        zIndex: 1000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "calc(var(--vh,1vh) * 100)",
        backgroundColor: backgroundColor,
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "none",
      }}
    >
      <Stack
        direction={"column"}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "660px",
          minHeight: "100%",
          pb: "20px",
          position: "relative",
        }}
      >
        {childrenType}

        <Stack
          direction={"row"}
          spacing={"20px"}
          justifyContent="center"
          sx={{
            mt: "auto",
            mb: "40px",
            pb: "10px",
            pt: 4,
            wordBreak: "keep-all",
            flexShrink: 0,
          }}
        >
          <Link
            opacity={100}
            bold={false}
            text={"사업자 정보"}
            url={companyInfoUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"개인정보 처리방침"}
            url={privacyPolicyUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"서비스 이용약관"}
            url={servicePolicyUrl}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
